import React from "react";
import styles from "./ChatEvents.module.css";
import {
    BookedCallBackChatEventDTO,
    BookedServiceCallBackChatEventDTO,
    BookedTestDriveChatEventDTO,
    ChatEventDTO,
    EventType,
    RequestedCallbackChatEventDTO,
    RequestedServiceCallbackChatEventDTO,
    RequestedTestDriveChatEventDTO,
    ReservedVehicleChatEventDTO,
    ReturnedFinanceApplicationChatEventDTO,
    ReturnedFinanceQuoteChatEventDTO,
    ReturnedVehicleValuationChatEventDTO,
    ReturnedFuelCostChatEventDTO,
    SupportedTimezone,
    UserDTO
} from "../../../../../api/generated";
import {FollowUpChatEventCard} from "./cards/FollowUpChatEventCard";
import {ReservationChatEventCard} from "./cards/ReservationChatEventCard";
import {FinanceQuotationChatEventCard} from "./cards/FinanceQuotationChatEventCard";
import {FinanceApplicationChatEventCard} from "./cards/FinanceApplicationChatEventCard";
import {CustomerVehicleValuationChatEventCard} from "./cards/CustomerVehicleValuationChatEventCard";
import classnames from "classnames";
import {ServiceCallbackChatEventCard} from "./cards/ServiceCallbackChatEventCard";
import {FuelCostChatEventCard} from "./cards/FuelCostChatEventCard";

interface ChatEventsProps {
    user: UserDTO
    chatEvents: ChatEventDTO[]
    timezone?: SupportedTimezone
}

export const ChatEvents: React.FC<ChatEventsProps> = ({
                                                          user,
                                                          chatEvents,
                                                          timezone
                                                      }) => {
    const visibleChatEvents = [
        EventType.RequestedCallback,
        EventType.RequestedTestDrive,
        EventType.RequestedEmailFollowUp,
        EventType.RequestedServiceCallback,
        EventType.BookedTestDrive,
        EventType.BookedCallBack,
        EventType.BookedServiceCallBack,
        EventType.BookedEmailFollowUp,
        EventType.ReservedVehicle,
        EventType.ReturnedFinanceQuote,
        EventType.ReturnedFinanceApplication,
        EventType.ReturnedVehicleValuation,
        EventType.ReturnedFuelCost
    ] as EventType []

    const isAnyCardToShow = !!chatEvents.filter(ce => visibleChatEvents.includes(ce.event_type)).length

    if (isAnyCardToShow) {
        return <div className={styles.wrapper}>
            {chatEvents
                .sort((e1, e2) => e2.id - e1.id)
                .map(chatEvent => {
                    const eventType = chatEvent.event_type
                    switch (eventType) {
                        case EventType.RequestedCallback:
                            return <FollowUpChatEventCard
                                eventType={chatEvent.event_type}
                                preferredDateTime={(chatEvent as RequestedCallbackChatEventDTO).preferred_date_time}
                            />
                        case EventType.RequestedTestDrive: {
                            const requestedTestDrive = chatEvent as RequestedTestDriveChatEventDTO;
                            return <FollowUpChatEventCard
                                eventType={chatEvent.event_type}
                                preferredDateTime={requestedTestDrive.preferred_date_time}
                                vrm={requestedTestDrive.stock_vehicle_vrm}
                                vehicleReference={[
                                    requestedTestDrive.vehicle_make,
                                    requestedTestDrive.vehicle_model,
                                    requestedTestDrive.vehicle_variant,
                                    requestedTestDrive.vds_internal_id
                                ].filter(Boolean).join(" ")}
                                rawVehicleDetails={requestedTestDrive.test_drive_vehicle_details}
                            />
                        }
                        case EventType.BookedTestDrive:
                            return <FollowUpChatEventCard
                                eventType={chatEvent.event_type}
                                eventDateTime={(chatEvent as BookedTestDriveChatEventDTO).booking_datetime}
                                timezone={timezone}
                                vrm={(chatEvent as BookedTestDriveChatEventDTO).stock_vehicle_vrm}
                                vehicleReference={[
                                    (chatEvent as BookedTestDriveChatEventDTO).vehicle_make,
                                    (chatEvent as BookedTestDriveChatEventDTO).vehicle_model,
                                    (chatEvent as BookedTestDriveChatEventDTO).vehicle_variant
                                ].filter(Boolean).join(" ")}
                                isCancelled={(chatEvent as BookedTestDriveChatEventDTO).isCanceled}
                            />
                        case EventType.BookedCallBack:
                            return <FollowUpChatEventCard
                                eventType={chatEvent.event_type}
                                eventDateTime={(chatEvent as BookedCallBackChatEventDTO).booking_datetime}
                                timezone={timezone}
                                isCancelled={(chatEvent as BookedCallBackChatEventDTO).isCanceled}
                            />
                        case EventType.BookedServiceCallBack:
                        case EventType.RequestedServiceCallback:
                            return <ServiceCallbackChatEventCard
                                serviceCallbackChatEvent={chatEvent as BookedServiceCallBackChatEventDTO | RequestedServiceCallbackChatEventDTO}
                            />
                        case EventType.RequestedEmailFollowUp:
                        case EventType.BookedEmailFollowUp:
                            return <FollowUpChatEventCard
                                eventType={chatEvent.event_type}
                            />
                        case EventType.ReservedVehicle:
                            return <ReservationChatEventCard
                                reservedVehicleChatEvent={chatEvent as ReservedVehicleChatEventDTO}
                                timezone={timezone}
                            />
                        case EventType.ReturnedFinanceQuote:
                            return <FinanceQuotationChatEventCard
                                returnedFinanceQuoteChatEvent={chatEvent as ReturnedFinanceQuoteChatEventDTO}
                            />
                        case EventType.ReturnedFinanceApplication:
                            return <FinanceApplicationChatEventCard
                                user={user}
                                returnedFinanceApplicationChatEvent={chatEvent as ReturnedFinanceApplicationChatEventDTO}
                            />
                        case EventType.ReturnedVehicleValuation:
                            return <CustomerVehicleValuationChatEventCard
                                returnedVehicleValuationChatEvent={chatEvent as ReturnedVehicleValuationChatEventDTO}
                            />
                        case EventType.ReturnedFuelCost:
                            return <FuelCostChatEventCard
                                returnedFuelCostChatEvent={chatEvent as ReturnedFuelCostChatEventDTO}
                            />
                        default:
                            return <></>
                    }
                })}
        </div>
    } else {
        return <span className={classnames(styles.wrapper, styles.noEventsToShow)}>
            Chat events will appear here
        </span>
    }
}
